import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Clock, Eye, Tag, ChevronRight, ArrowLeft, Twitter, Linkedin, Facebook } from "lucide-react";
import { SEO } from "@/components/SEO";
import API from "@/lib/api";
import { WhatsAppButton } from "@/components/WhatsAppButton";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPost();
  }, [slug]);

  const loadPost = async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/blogs/${slug}`);
      setPost(data);
      // Load related posts from same category
      const relRes = await API.get(`/blogs?category=${encodeURIComponent(data.category)}&limit=3`);
      setRelated((relRes.data.blogs || []).filter(b => b._id !== data._id).slice(0, 3));
    } catch {
      navigate("/blog");
    } finally { setLoading(false); }
  };

  const shareUrl = `https://www.sendoras.com/blog/${slug}`;

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#0c1e41] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!post) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title={post.title}
        description={post.excerpt}
        canonical={`/blog/${post.slug}`}
        image={post.image_url}
        type="article"
        keywords={post.tags?.join(", ")}
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "description": post.excerpt,
          "image": post.image_url,
          "author": { "@type": "Organization", "name": post.author || "Sendora Team" },
          "publisher": {
            "@type": "Organization",
            "name": "Sendora",
            "logo": { "@type": "ImageObject", "url": "https://www.sendoras.com/sendora-logo.webp" }
          },
          "datePublished": post.created_at,
          "dateModified": post.updated_at,
          "url": `https://www.sendoras.com/blog/${post.slug}`,
        }}
      />

      {/* Top nav */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/">
            <img src="/sendora-logo.webp" alt="Sendora" className="h-7 w-auto" />
          </Link>
          <ChevronRight size={14} className="text-slate-300" />
          <Link to="/blog" className="text-sm text-slate-500 hover:text-[#0c1e41]">Blog</Link>
          <ChevronRight size={14} className="text-slate-300" />
          <span className="text-sm text-slate-700 font-medium line-clamp-1 max-w-xs hidden sm:block">{post.title}</span>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 py-10">
        {/* Back */}
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0c1e41] mb-6 group">
          <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" /> Back to Blog
        </Link>

        {/* Category + meta */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="text-xs font-bold bg-[#eef1f8] text-[#0c1e41] px-3 py-1 rounded-full uppercase tracking-wide">
            {post.category}
          </span>
          <span className="text-xs text-slate-400 flex items-center gap-1"><Clock size={11} /> {post.read_time} min read</span>
          <span className="text-xs text-slate-400 flex items-center gap-1"><Eye size={11} /> {(post.views || 0).toLocaleString()} views</span>
        </div>

        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 leading-tight mb-4">{post.title}</h1>
        <p className="text-lg text-slate-500 mb-6 leading-relaxed">{post.excerpt}</p>

        {/* Author + date */}
        <div className="flex items-center gap-3 mb-8 pb-6 border-b border-slate-200">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0c1e41] to-blue-600 flex items-center justify-center text-white text-sm font-bold shrink-0">
            {(post.author || "S")[0]}
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800">{post.author || "Sendora Team"}</p>
            <p className="text-xs text-slate-400">
              {post.created_at ? new Date(post.created_at).toLocaleDateString("en-NG", { day: "numeric", month: "long", year: "numeric" }) : ""}
            </p>
          </div>
        </div>

        {/* Featured image */}
        {post.image_url && (
          <div className="rounded-2xl overflow-hidden mb-8 aspect-[16/9]">
            <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
          </div>
        )}

        {/* Content */}
        <div
          data-testid="blog-post-content"
          className="prose prose-slate max-w-none prose-headings:text-[#0c1e41] prose-headings:font-bold prose-a:text-[#0c1e41] prose-code:bg-slate-100 prose-code:px-1 prose-code:rounded prose-pre:bg-slate-900 prose-pre:text-slate-100"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* Tags */}
        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-slate-200">
            <Tag size={14} className="text-slate-400 mt-0.5" />
            {post.tags.map(tag => (
              <span key={tag} className="text-xs text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">{tag}</span>
            ))}
          </div>
        )}

        {/* Share */}
        <div className="mt-8 pt-6 border-t border-slate-200 flex items-center gap-4">
          <span className="text-sm font-medium text-slate-600">Share:</span>
          <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-[#0c1e41] transition-colors">
            <Twitter size={16} />
          </a>
          <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-[#0c1e41] transition-colors">
            <Linkedin size={16} />
          </a>
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-[#0c1e41] transition-colors">
            <Facebook size={16} />
          </a>
        </div>
      </article>

      {/* Related posts */}
      {related.length > 0 && (
        <div className="bg-white border-t border-slate-200 py-12 mt-4">
          <div className="max-w-4xl mx-auto px-4">
            <h3 className="text-xl font-bold text-slate-900 mb-6">More from {post.category}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {related.map(r => (
                <Link key={r._id} to={`/blog/${r.slug}`} className="group bg-slate-50 rounded-xl overflow-hidden hover:shadow-md transition-all border border-slate-200">
                  {r.image_url && (
                    <div className="aspect-[16/9] overflow-hidden">
                      <img src={r.image_url} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    </div>
                  )}
                  <div className="p-4">
                    <p className="text-sm font-semibold text-slate-800 line-clamp-2 group-hover:text-[#0c1e41]">{r.title}</p>
                    <p className="text-xs text-slate-400 mt-1 flex items-center gap-1"><Clock size={10} /> {r.read_time} min</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      <footer className="bg-[#0c1e41] text-white/60 text-center text-xs py-6">
        <p>© {new Date().getFullYear()} Sendora · <Link to="/blog" className="hover:text-white">Blog</Link> · <Link to="/privacy" className="hover:text-white">Privacy</Link></p>
      </footer>
      <WhatsAppButton />
    </div>
  );
}
